import { administrationsPerInterval, isHeld, isPrn, timesPerInterval } from '@/common/medicationSchedule';

/**
 * The form shape behind one administration window — a schedule segment or a taper step.
 *
 * Both are the same thing to the resolver, so both forms ask the same questions and this
 * is where the answers live between the fields and buildSegment. What differs between the
 * two is only what bounds the window: a segment states a start and an ends[x], a taper
 * step states a 'Continue for' and takes its start from the chain.
 */

export const INTERVAL_UNIT_OPTIONS = [
    { value: 'd', title: 'Day(s)' },
    { value: 'wk', title: 'Week(s)' },
];

export const MINIMUM_INTERVAL_UNIT_OPTIONS = [
    { value: 'h', title: 'Hours' },
    { value: 'min', title: 'Minutes' },
];

// A dose given within half an hour of its time is given on time.
export const DEFAULT_ADMINISTRATION_WINDOW = 30;

/** The window a new schedule opens on: the account's, or the fallback when it authored none. */
export const accountAdministrationWindow = (account) => {
    const minutes = Number(account?.medPassWindowMinutes);
    return Number.isFinite(minutes) && minutes > 0 ? minutes : DEFAULT_ADMINISTRATION_WINDOW;
};

export const HOLD_REASON_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-hold-reasons';
export const DISCONTINUE_REASON_SYSTEM = 'http://fhir.bestnotes.com/r4/CodeSystem/medication-discontinue-reasons';

/**
 * Concepts as select items.
 *
 * @param {Array<object>} concepts
 * @returns {Array<{value: string, title: string}>}
 */
export const reasonItems = (concepts) => (concepts || []).map((concept) => ({ value: concept.code, title: concept.display || concept.code })).sort((a, b) => a.title.localeCompare(b.title));

/**
 * The value a reason control holds for a stored CodeableConcept — the concept code when it
 * was chosen from the list, the text itself when it was typed.
 *
 * @param {object} reason - a CodeableConcept
 * @returns {string|null}
 */
export const reasonValueOf = (reason) => reason?.coding?.[0]?.code || reason?.text || null;

/**
 * A reason as the CodeableConcept to store.
 *
 * A reason typed rather than chosen is still a reason — recorded as text with no coding,
 * rather than as a code invented for it. That also means the field keeps working when the
 * concept list is empty, which is what a terminology migration not yet applied looks like.
 *
 * @param {Array<object>} concepts
 * @param {string} code - a concept code, or free text
 * @param {string} system
 * @returns {object|undefined}
 */
export const reasonConceptOf = (concepts, code, system) => {
    if (!code) return undefined;
    const concept = (concepts || []).find((item) => item.code === code);
    if (!concept) return { text: String(code) };
    return { coding: [{ system, code: concept.code, display: concept.display }], text: concept.display };
};

/**
 * The hold-reason concepts as select items.
 *
 * @param {Array<object>} concepts
 * @returns {Array<{value: string, title: string}>}
 */
export const holdReasonItems = (concepts) => reasonItems(concepts);

/**
 * The value a hold reason control holds for `segment`. One reader, because the dialog needs
 * it separately from windowFromSegment and two copies drift.
 *
 * @param {object} segment
 * @returns {string|null}
 */
export const holdReasonValueOf = (segment) => reasonValueOf(segment?.hold?.reason);

/**
 * A hold reason as the CodeableConcept a hold segment stores.
 *
 * @param {Array<object>} concepts
 * @param {string} code - a concept code, or free text
 * @returns {object|undefined}
 */
export const holdReasonConceptOf = (concepts, code) => reasonConceptOf(concepts, code, HOLD_REASON_SYSTEM);

const DAY_UNIT = 'd';

/**
 * How many administrations the window's slots amount to per interval — counting the slots,
 * not the times entered into them, so a window still being filled in seeds its stated
 * frequency rather than zero.
 *
 * @param {object} window
 * @returns {number}
 */
export const windowFrequency = (window) => {
    const days = usesDayOfWeek(window) ? (window?.dayOfWeek || []).length || 1 : 1;
    return ((window?.times || []).length || 1) * days;
};

/**
 * A window with nothing answered. `times` opens with as many empty slots as the order
 * calls for, which is what makes an order of twice daily open with two.
 *
 * @param {object} [overrides]
 * @returns {object}
 */
export const blankWindow = (overrides = {}) => ({
    type: 'scheduled',
    period: 1,
    periodUnit: DAY_UNIT,
    // What the window SAYS it gives per interval, which is not what its slots currently
    // amount to. Adding a time does not restate it — the mismatch message is what speaks to
    // a count that no longer matches.
    frequency: 1,
    times: [null],
    dayOfWeek: [],
    administrationWindow: DEFAULT_ADMINISTRATION_WINDOW,
    selfAdministered: false,
    maxPerDay: null,
    minimumIntervalValue: null,
    minimumIntervalUnit: 'h',
    prnIndication: '',
    holdReasonCode: null,
    holdNotes: '',
    ...overrides,
});

/**
 * Which type a stored segment is. A PRN or a hold carries no timing pattern, so without
 * this they read as a schedule whose times were simply left off.
 *
 * @param {object} segment
 * @returns {'scheduled'|'prn'|'hold'}
 */
export const windowTypeOf = (segment) => {
    if (isHeld(segment)) return 'hold';
    if (isPrn(segment)) return 'prn';
    return 'scheduled';
};

/**
 * Read a stored segment back into the form shape.
 *
 * Dates are deliberately absent: what bounds a window belongs to the host form, and a
 * segment seeded from another one never inherits its dates.
 *
 * @param {object} segment - a Dosage, or Dosage-shaped JSON
 * @param {object} [overrides]
 * @returns {object}
 */
export const windowFromSegment = (segment, overrides = {}) => {
    const repeat = segment?.timing?.repeat;
    const times = (repeat?.timeOfDay || []).map((time) => String(time).slice(0, 5));
    return blankWindow({
        type: windowTypeOf(segment),
        period: Number(repeat?.period) > 0 ? Number(repeat.period) : 1,
        periodUnit: repeat?.periodUnit || DAY_UNIT,
        times: times.length ? times : [null],
        dayOfWeek: [...(repeat?.dayOfWeek || [])],
        // Nothing stores a frequency — buildSegment writes timeOfDay and dayOfWeek and the
        // resolver counts them — so a window read back states what its slots amount to.
        frequency: windowFrequency({ times, dayOfWeek: repeat?.dayOfWeek || [], periodUnit: repeat?.periodUnit || DAY_UNIT }),
        administrationWindow: segment?.timing?.administrationWindow ?? null,
        selfAdministered: segment?.selfAdministered === true,
        maxPerDay: segment?.maxPerDay ?? null,
        minimumIntervalValue: segment?.minimumInterval?.value ?? null,
        minimumIntervalUnit: segment?.minimumInterval?.code || 'h',
        prnIndication: segment?.asNeeded?.CodeableConcept?.text || '',
        // A hold states its own reason every time; it is never carried over from another window.
        holdReasonCode: holdReasonValueOf(segment),
        holdNotes: segment?.hold?.notes || '',
        ...overrides,
    });
};

export const filledTimes = (window) => (window?.times || []).filter(Boolean);

/**
 * The window as a Timing.repeat, for the helpers that read a recurrence —
 * administrationsPerInterval, timesPerInterval, nextOccurrences.
 *
 * @param {object} window
 * @returns {object}
 */
export const windowRepeat = (window) => ({
    timeOfDay: filledTimes(window),
    dayOfWeek: [...(normalizeWindow(window)?.dayOfWeek || [])],
    period: Number(window?.period) > 0 ? Number(window.period) : 1,
    periodUnit: window?.periodUnit || DAY_UNIT,
});

/**
 * A day interval is one calendar day, so there is nothing for a weekday list to select.
 *
 * @param {object} window
 * @returns {boolean}
 */
export const usesDayOfWeek = (window) => (window?.periodUnit || DAY_UNIT) !== DAY_UNIT;

/**
 * Drop a weekday list a day interval cannot mean.
 *
 * A window that repeated weekly and is switched back to a day interval leaves its weekdays
 * behind, and nothing downstream refuses them: buildSegment writes dayOfWeek whatever the
 * unit is, and occursOn applies that filter BEFORE it looks at the unit. A step the
 * clinician set to 'every day' would administer on Mon/Wed/Fri alone — with the day toggle
 * hidden, so nothing on screen says so. Normalising here is what keeps the stored
 * recurrence the one the form describes.
 *
 * @param {object} window
 * @returns {object}
 */
export const normalizeWindow = (window) => (!window || usesDayOfWeek(window) ? window : { ...window, dayOfWeek: [] });

/**
 * The one unit vocabulary. Singular; `unitName(code, count)` pluralises. Three maps had
 * drifted apart across the window module and the two dialogs, so a new unit had to be found
 * and added in three places with nothing tying them together.
 */
const UNIT_NAMES = { min: 'minute', h: 'hour', d: 'day', wk: 'week', mo: 'month', a: 'year' };

/**
 * A period unit in words, pluralised for `count`.
 *
 * @param {string} code - 'min' | 'h' | 'd' | 'wk' | 'mo' | 'a'
 * @param {number} [count=1]
 * @returns {string}
 */
export const unitName = (code, count = 1) => {
    const name = UNIT_NAMES[code] || code || '';
    return Math.abs(Number(count)) === 1 ? name : `${name}s`;
};

/**
 * The window's interval in words — 'every day', 'every 2 weeks'.
 *
 * @param {object} window
 * @returns {string}
 */
export const intervalLabel = (window) => {
    const period = Number(window?.period) > 0 ? Number(window.period) : 1;
    return period === 1 ? `every ${unitName(window?.periodUnit)}` : `every ${period} ${unitName(window?.periodUnit, period)}`;
};

/**
 * How many administrations this window produces per repetition of its interval.
 *
 * @param {object} window
 * @returns {number}
 */
export const windowPerInterval = (window) => administrationsPerInterval(windowRepeat(window));

/**
 * Grow the window's time slots to what `frequency` needs, given the days it repeats on.
 * Never discards a time already entered — the frequency mismatch message is what speaks
 * to a wrong count.
 *
 * @param {object} window
 * @param {number} frequency
 * @returns {string[]} the times the window should now carry
 */
export const grownTimes = (window, frequency) => {
    const needed = timesPerInterval(frequency, windowRepeat(window));
    const entered = filledTimes(window);
    if (entered.length >= needed) return window?.times || [];
    return [...entered, ...Array.from({ length: needed - entered.length }, () => null)];
};

/**
 * Resize the window's time slots to `frequency` administrations per interval.
 *
 * Unlike grownTimes this also trims, from the end: stating a lower frequency is a decision
 * to drop the last dose of the interval, where a change of days or of the order's frequency
 * is not — those grow only, and the mismatch message speaks to a wrong count.
 *
 * @param {object} window
 * @param {number} frequency
 * @returns {Array<string|null>}
 */
export const resizeTimes = (window, frequency) => {
    const needed = timesPerInterval(frequency, windowRepeat(window));
    const times = window?.times || [];
    if (times.length === needed) return times;
    if (times.length > needed) return times.slice(0, needed);
    return [...times, ...Array.from({ length: needed - times.length }, () => null)];
};

const minimumIntervalOf = (window) => {
    const value = Number(window?.minimumIntervalValue);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    const code = window?.minimumIntervalUnit || 'h';
    return { value, unit: code === 'h' ? 'hours' : 'minutes', system: 'http://unitsofmeasure.org', code };
};

/**
 * Whether the window answers everything its own type requires.
 *
 * Mirrors what buildSegment throws on, so no throw is reachable through a form.
 *
 * @param {object} window
 * @returns {boolean}
 */
export const isWindowComplete = (window) => {
    if (!window) return false;
    if (window.type === 'prn') return Number(window.maxPerDay) > 0 && Number(window.minimumIntervalValue) > 0;
    if (window.type === 'hold') return Boolean(window.holdReasonCode);
    if (!(Number(window.period) > 0)) return false;
    if (!filledTimes(window).length) return false;
    return !usesDayOfWeek(window) || (window.dayOfWeek || []).length > 0;
};

/**
 * Does the window still give what it says it gives?
 *
 * The stated frequency is a decision; the times are how it is met. Adding or clearing a
 * time changes the second and not the first, so the two can disagree — and a step that
 * disagrees would administer a count nobody chose.
 *
 * @param {object} window
 * @returns {boolean}
 */
export const windowFrequencyMismatch = (window) => {
    if (!window || window.type !== 'scheduled') return false;
    const stated = Number(window.frequency);
    if (!Number.isFinite(stated) || stated < 1) return false;
    return windowPerInterval(window) !== stated;
};

/**
 * The window as buildSegment arguments. What bounds it — start, ends, stepDuration — is
 * the host form's to add.
 *
 * @param {object} window
 * @param {{holdReason?: object}} [options] - the resolved hold reason CodeableConcept
 * @returns {object}
 */
export const windowSegmentInput = (window, { holdReason } = {}) => ({
    type: window?.type || 'scheduled',
    timeOfDay: filledTimes(window),
    dayOfWeek: [...(normalizeWindow(window)?.dayOfWeek || [])],
    period: Number(window?.period) > 0 ? Number(window.period) : 1,
    periodUnit: window?.periodUnit || DAY_UNIT,
    administrationWindow: Number(window?.administrationWindow) > 0 ? Number(window.administrationWindow) : undefined,
    minimumInterval: minimumIntervalOf(window),
    maxPerDay: Number(window?.maxPerDay) > 0 ? Number(window.maxPerDay) : undefined,
    asNeeded: window?.prnIndication ? { text: window.prnIndication } : undefined,
    hold: window?.type === 'hold' ? { reason: holdReason, notes: window.holdNotes || undefined } : undefined,
    selfAdministered: window?.selfAdministered === true || undefined,
});

export default {
    INTERVAL_UNIT_OPTIONS,
    MINIMUM_INTERVAL_UNIT_OPTIONS,
    DEFAULT_ADMINISTRATION_WINDOW,
    accountAdministrationWindow,
    HOLD_REASON_SYSTEM,
    holdReasonItems,
    holdReasonConceptOf,
    holdReasonValueOf,
    blankWindow,
    windowTypeOf,
    windowFromSegment,
    filledTimes,
    windowRepeat,
    usesDayOfWeek,
    normalizeWindow,
    unitName,
    intervalLabel,
    windowPerInterval,
    grownTimes,
    resizeTimes,
    windowFrequency,
    windowFrequencyMismatch,
    isWindowComplete,
    windowSegmentInput,
};

export const DAY_NAMES = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

/**
 * A segment's first day, 'YYYY-MM-DD', or null.
 *
 * @param {object} segment
 * @returns {string|null}
 */
export const segmentStartDay = (segment) => String(segment?.timing?.repeat?.boundsPeriod?.start || '').slice(0, 10) || null;

/**
 * A segment's stated last day, 'YYYY-MM-DD', or null.
 *
 * @param {object} segment
 * @returns {string|null}
 */
export const segmentEndDay = (segment) => String(segment?.timing?.repeat?.boundsPeriod?.end || '').slice(0, 10) || null;

/**
 * The one place the patient is enrolled at, or null.
 *
 * Null where there are two: which office a dose lands at is the question the site field exists to
 * answer. Enrolled, not merely offered - the list also carries the finer places under each one.
 *
 * @param {Array<{id: string, enrollment?: boolean}>} sites
 * @returns {string|null}
 */
export const enrolledSiteOf = (sites) => {
    const enrolled = (sites || []).filter((site) => site.enrollment);
    return enrolled.length === 1 ? enrolled[0].id : null;
};
